import tw from "tailwind-styled-components";
import { MdClose, MdTempleBuddhist } from "react-icons/md";
import { FaBuilding, FaUser, FaPhone, FaMapMarkerAlt, FaRupeeSign, FaUtensils, FaUsers, FaBed, FaRuler, FaParking, FaShieldAlt, FaHospital, FaBus, FaTrain, FaPlane, FaStore, FaDumbbell, FaFireExtinguisher } from "react-icons/fa";
import { GiSmokeBomb } from "react-icons/gi";
import { BsLightningCharge, BsBuilding } from "react-icons/bs";
import { MdOutlineSecurity, MdOutlineLibraryBooks, MdOutlineElevator } from "react-icons/md";
import moment from "moment";
import { ScrollableContent } from "../Styles/InputStyles";
import { ProfileSection } from "../Styles/PageStyles";

const getValueOrDashedIcon = (value) => {
    if (value === null || value === undefined || value === "Invalid date" || value === "" || value === 0) {
        return <span className="text-gray-400">-</span>;
    }
    return value;
};

const getValidDateOrDashedIcon = (date) => {
    if (!date) {
        return <span className="text-gray-400">-</span>;
    }

    const formattedDate = moment(date);

    if (!formattedDate.isValid()) {
        return <span className="text-gray-400">—</span>;
    }

    return formattedDate.format("DD-MMM-YY");
};

const getAvailabilityStatus = (status) => {
    if (status === "available") {
        return <p className="bg-green-50 text-green-700 text-xs border-green-100 border w-20 h-7 flex items-center justify-center rounded-lg">Available</p>;
    } else if (status === "hold") {
        return <p className="bg-yellow-50 text-yellow-700 text-xs border-yellow-100 border w-20 h-7 flex items-center justify-center rounded-lg">On Hold</p>;
    } else if (status === "closed") {
        return <p className="bg-red-50 text-red-700 text-xs border-red-100 border w-20 h-7 flex items-center justify-center rounded-lg">Closed</p>;
    } else {
        return <p className="bg-gray-50 text-gray-700 text-xs border-gray-100 border w-20 h-7 flex items-center justify-center rounded-lg">Unknown</p>;
    }
};

const getListingType = (type) => {
    const types = {
        'RENT': { label: 'For Rent', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
        'SALE': { label: 'For Sale', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-100' },
        'LEASE': { label: 'For Lease', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-100' }
    };
    const style = types[type] || types['RENT'];
    return (
        <p className={`${style.bg} ${style.text} text-xs ${style.border} border w-20 h-7 flex items-center justify-center rounded-lg`}>
            {style.label}
        </p>
    );
};

const AmenityIcon = ({ label, icon: Icon, isAvailable }) => {
    if (!isAvailable) return null;
    return (
        <div className="flex items-center gap-1 bg-blue-50 text-primary-color px-2 py-1 rounded-lg text-xs">
            <Icon size={12} />
            <span>{label}</span>
        </div>
    );
};

const ConnectivityIcon = ({ label, icon: Icon, isAvailable }) => {
    if (!isAvailable) return null;
    return (
        <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-lg text-xs">
            <Icon size={12} />
            <span>{label}</span>
        </div>
    );
};

const SecurityIcon = ({ label, icon: Icon, isAvailable }) => {
    if (!isAvailable) return null;
    return (
        <div className="flex items-center gap-1 bg-red-50 text-red-700 px-2 py-1 rounded-lg text-xs">
            <Icon size={12} />
            <span>{label}</span>
        </div>
    );
};

const ViewDetails = ({
    setViewDetail,
    selectedData,
}) => {
    const onClose = () => {
        setViewDetail(false);
    };

    return (
        <Container>
            <div className="flex items-center justify-between bg-[#26A69A] py-4 px-4">
                <Title className="text-white">{"Property Details"}</Title>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </div>

            <ProfileSection className="flex justify-between">
                <UserImageWrapper>
                    <div className="flex flex-col gap-1">
                        <span className="text-gray-600 text-sm font-medium flex items-center gap-2">
                            {selectedData?.buildingName}
                        </span>
                        <span className="text-gray-400 text-xs flex items-center gap-1">
                            {selectedData?.address}, {selectedData?.locationName} - {selectedData?.pinCode}
                        </span>
                    </div>
                </UserImageWrapper>

                <div className="flex gap-2">
                    {getListingType(selectedData?.listingType)}
                    {getAvailabilityStatus(selectedData?.availabilityStatus)}
                </div>
            </ProfileSection>

            <Content>
                <ScrollableContent>
                    {/* Owner Details Section */}
                    <Wrapper>
                        <SectionTitle>Owner Details</SectionTitle>
                        <Section className="mb-3">
                            <Row>
                                <FlexCol>
                                    <Label>Name</Label>
                                    <Value>{getValueOrDashedIcon(selectedData?.ownerName)}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Mobile</Label>
                                    <Value>{getValueOrDashedIcon(selectedData?.ownerMobile)}</Value>
                                </FlexCol>
                            </Row>
                            <FlexCol>
                                <Label>Ownership Role</Label>
                                <Value className="capitalize">{getValueOrDashedIcon(selectedData?.ownershipRole)}</Value>
                            </FlexCol>
                        </Section>
                    </Wrapper>

                    {/* Property Details Section */}
                    <Wrapper>
                        <SectionTitle>Property Details</SectionTitle>
                        <Section className="mb-3">
                            <Row>
                                <FlexCol>
                                    <Label>Property Type</Label>
                                    <Value className="capitalize">{getValueOrDashedIcon(selectedData?.propertyType)}</Value>
                                </FlexCol>
                                {selectedData?.bhk && (
                                    <FlexCol>
                                        <Label>BHK</Label>
                                        <Value>{getValueOrDashedIcon(selectedData?.bhk)}</Value>
                                    </FlexCol>
                                )}
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Area</Label>
                                    <Value>{getValueOrDashedIcon(selectedData?.area)}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Furnished Type</Label>
                                    <Value className="capitalize">{getValueOrDashedIcon(selectedData?.furnishedType)}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Parking</Label>
                                    <Value className="capitalize">{getValueOrDashedIcon(selectedData?.parking)}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Deals Count</Label>
                                    <Value>{getValueOrDashedIcon(selectedData?.dealsCount)}</Value>
                                </FlexCol>
                            </Row>
                        </Section>
                    </Wrapper>

                    {/* Pricing Section */}
                    <Wrapper>
                        <SectionTitle>Pricing Details</SectionTitle>
                        <Section className="mb-3">
                            {selectedData?.listingType === 'RENT' ? (
                                <>
                                    <Row>
                                        <FlexCol>
                                            <Label>Monthly Rent</Label>
                                            <Value>₹ {getValueOrDashedIcon(selectedData?.monthlyRent?.toLocaleString())}</Value>
                                        </FlexCol>
                                        <FlexCol>
                                            <Label>Security Deposit</Label>
                                            <Value>₹ {getValueOrDashedIcon(selectedData?.securityDeposit?.toLocaleString())}</Value>
                                        </FlexCol>
                                    </Row>
                                    <Row>
                                        <FlexCol>
                                            <Label>Maintenance</Label>
                                            <Value>₹ {getValueOrDashedIcon(selectedData?.maintenanceCharges?.toLocaleString())}</Value>
                                        </FlexCol>
                                        <FlexCol>
                                            <Label>Food Preference</Label>
                                            <Value className="capitalize">{getValueOrDashedIcon(selectedData?.foodPreference)}</Value>
                                        </FlexCol>
                                    </Row>
                                    <Row>
                                        <FlexCol>
                                            <Label>Preferred Tenant</Label>
                                            <Value className="capitalize">{getValueOrDashedIcon(selectedData?.preferredTenantType)}</Value>
                                        </FlexCol>
                                        <FlexCol>
                                            <Label>Created At</Label>
                                            <Value>{getValidDateOrDashedIcon(selectedData?.createdAt)}</Value>
                                        </FlexCol>
                                    </Row>
                                </>
                            ) : (
                                <Row>
                                    <FlexCol>
                                        <Label>{selectedData?.listingType === 'SALE' ? 'Selling Amount' : 'Lease Amount'}</Label>
                                        <Value>₹ {getValueOrDashedIcon(
                                            selectedData?.listingType === 'SALE'
                                                ? selectedData?.sellingAmount?.toLocaleString()
                                                : selectedData?.leaseAmount?.toLocaleString()
                                        )}</Value>
                                    </FlexCol>
                                    <FlexCol>
                                        <Label>Created At</Label>
                                        <Value>{getValidDateOrDashedIcon(selectedData?.createdAt)}</Value>
                                    </FlexCol>

                                </Row>
                            )}
                        </Section>
                    </Wrapper>

                    {/* Location & Connectivity */}
                    <Wrapper>
                        <SectionTitle>Location & Connectivity</SectionTitle>
                        <Section className="mb-3">
                            <div className="flex flex-wrap gap-2">
                                <ConnectivityIcon label="Temple" icon={MdTempleBuddhist} isAvailable={selectedData?.closeToTemple} />
                                <ConnectivityIcon label="Hospital" icon={FaHospital} isAvailable={selectedData?.closeToHospital} />
                                <ConnectivityIcon label="Bus Stop" icon={FaBus} isAvailable={selectedData?.closeToBusStop} />
                                <ConnectivityIcon label="Railway Station" icon={FaTrain} isAvailable={selectedData?.closeToRailwayStation} />
                                <ConnectivityIcon label="Airport" icon={FaPlane} isAvailable={selectedData?.closeToAirport} />
                            </div>
                        </Section>
                    </Wrapper>

                    {/* Amenities */}
                    <Wrapper>
                        <SectionTitle>Amenities</SectionTitle>
                        <Section className="mb-3">
                            <div className="flex flex-wrap gap-2">
                                <AmenityIcon label="Power Backup" icon={BsLightningCharge} isAvailable={selectedData?.powerBackup} />
                                <AmenityIcon label="Lift" icon={MdOutlineElevator} isAvailable={selectedData?.lift} />
                                <AmenityIcon label="Gym" icon={FaDumbbell} isAvailable={selectedData?.gym} />
                                <AmenityIcon label="Club House" icon={FaBuilding} isAvailable={selectedData?.clubHouse} />
                                <AmenityIcon label="Library" icon={MdOutlineLibraryBooks} isAvailable={selectedData?.library} />
                            </div>
                        </Section>
                    </Wrapper>

                    {/* Security Features */}
                    <Wrapper>
                        <SectionTitle>Security Features</SectionTitle>
                        <Section className="mb-3">
                            <div className="flex flex-wrap gap-2">
                                <SecurityIcon label="Gated Community" icon={FaShieldAlt} isAvailable={selectedData?.gatedCommunity} />
                                <SecurityIcon label="24x7 Security" icon={MdOutlineSecurity} isAvailable={selectedData?.security247} />
                                <SecurityIcon label="Fire Fighting" icon={FaFireExtinguisher} isAvailable={selectedData?.fireFightingSystem} />
                                <SecurityIcon label="Smoke Detector" icon={GiSmokeBomb} isAvailable={selectedData?.smokeDetector} />
                            </div>
                        </Section>
                    </Wrapper>

                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Wrapper = tw.div`grid gap-1.5 my-0`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-600`;
const Section = tw.div` border border-gray-200 p-3 rounded-lg`;
const SectionTitle = tw.h2`text-base font-medium text-gray-600 flex justify-between items-center`;
const Row = tw.div`grid grid-cols-2 `;
const Label = tw.label`text-sm text-gray-400`;
const Value = tw.span`text-gray-600 text-base`;
const FlexCol = tw.div`flex flex-col gap-0.5`
const Content = tw.div`p-4 flex flex-col gap-4`;
const UserImageWrapper = tw.div` flex items-center gap-2`;

export default ViewDetails;